export interface ScriptExecutionResult {
  logs: string[];
  result: any;
  error?: string;
}

type ModuleCache = Record<string, any>;

const formatLogValue = (value: any): string => {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const normalizeModule = (moduleValue: any): any => {
  if (!moduleValue || typeof moduleValue !== 'object') return moduleValue;

  const keys = Object.keys(moduleValue).filter(key => key !== '__esModule');
  if (keys.length === 1 && keys[0] === 'default') {
    return moduleValue.default;
  }

  return moduleValue;
};

const isUrlLike = (specifier: string): boolean =>
  /^(?:https?:|data:|blob:|file:|\/)/.test(specifier);

const defaultResolveModuleSpecifier = (specifier: string, baseUrl: string): string => {
  if (specifier.startsWith('.')) {
    return new URL(specifier, baseUrl).href;
  }

  if (isUrlLike(specifier)) {
    return specifier.startsWith('/') ? new URL(specifier, baseUrl).href : specifier;
  }

  return `https://esm.sh/${specifier}`;
};

const stripCommentsAndStrings = (code: string): string => {
  let result = '';
  let inSingle = false;
  let inDouble = false;
  let inTemplate = false;
  let inLineComment = false;
  let inBlockComment = false;
  let escaped = false;

  for (let i = 0; i < code.length; i++) {
    const current = code[i];
    const next = code[i + 1];

    if (inLineComment) {
      if (current === '\n') {
        inLineComment = false;
        result += '\n';
      }
      continue;
    }

    if (inBlockComment) {
      if (current === '*' && next === '/') {
        inBlockComment = false;
        i++;
      }
      continue;
    }

    if (inSingle) {
      if (!escaped && current === '\'') inSingle = false;
      escaped = !escaped && current === '\\';
      result += ' ';
      continue;
    }

    if (inDouble) {
      if (!escaped && current === '"') inDouble = false;
      escaped = !escaped && current === '\\';
      result += ' ';
      continue;
    }

    if (inTemplate) {
      if (!escaped && current === '`') inTemplate = false;
      escaped = !escaped && current === '\\';
      result += current === '\n' ? '\n' : ' ';
      continue;
    }

    escaped = false;

    if (current === '/' && next === '/') {
      inLineComment = true;
      i++;
      continue;
    }

    if (current === '/' && next === '*') {
      inBlockComment = true;
      i++;
      continue;
    }

    if (current === '\'') {
      inSingle = true;
      result += ' ';
      continue;
    }

    if (current === '"') {
      inDouble = true;
      result += ' ';
      continue;
    }

    if (current === '`') {
      inTemplate = true;
      result += ' ';
      continue;
    }

    result += current;
  }

  return result;
};

const collectRequireSpecifiers = (code: string): string[] => {
  const sanitized = stripCommentsAndStrings(code);
  const matches = sanitized.matchAll(/\brequire\s*\(\s*(['"])([^'"]+)\1\s*\)/g);
  return Array.from(new Set(Array.from(matches, match => match[2])));
};

const skipWhitespaceAndComments = (code: string, index: number): number => {
  let current = index;

  while (current < code.length) {
    if (/\s/.test(code[current])) {
      current++;
      continue;
    }

    if (code[current] === '/' && code[current + 1] === '/') {
      current += 2;
      while (current < code.length && code[current] !== '\n') current++;
      continue;
    }

    if (code[current] === '/' && code[current + 1] === '*') {
      current += 2;
      while (current < code.length && !(code[current] === '*' && code[current + 1] === '/')) current++;
      current += 2;
      continue;
    }

    break;
  }

  return current;
};

const readImportStatement = (code: string, startIndex: number): { statement: string; endIndex: number } | null => {
  let index = skipWhitespaceAndComments(code, startIndex);
  if (!code.slice(index).startsWith('import')) {
    return null;
  }

  let i = index;
  let braceDepth = 0;
  let parenDepth = 0;
  let bracketDepth = 0;
  let inSingle = false;
  let inDouble = false;
  let inTemplate = false;
  let escaped = false;

  while (i < code.length) {
    const current = code[i];
    const next = code[i + 1];

    if (inSingle) {
      if (!escaped && current === '\'') inSingle = false;
      escaped = !escaped && current === '\\';
      i++;
      continue;
    }

    if (inDouble) {
      if (!escaped && current === '"') inDouble = false;
      escaped = !escaped && current === '\\';
      i++;
      continue;
    }

    if (inTemplate) {
      if (!escaped && current === '`') inTemplate = false;
      escaped = !escaped && current === '\\';
      i++;
      continue;
    }

    escaped = false;

    if (current === '/' && next === '/') {
      i += 2;
      while (i < code.length && code[i] !== '\n') i++;
      continue;
    }

    if (current === '/' && next === '*') {
      i += 2;
      while (i < code.length && !(code[i] === '*' && code[i + 1] === '/')) i++;
      i += 2;
      continue;
    }

    if (current === '\'') {
      inSingle = true;
      i++;
      continue;
    }

    if (current === '"') {
      inDouble = true;
      i++;
      continue;
    }

    if (current === '`') {
      inTemplate = true;
      i++;
      continue;
    }

    if (current === '{') braceDepth++;
    if (current === '}') braceDepth--;
    if (current === '(') parenDepth++;
    if (current === ')') parenDepth--;
    if (current === '[') bracketDepth++;
    if (current === ']') bracketDepth--;

    if (braceDepth === 0 && parenDepth === 0 && bracketDepth === 0) {
      if (current === ';') {
        return { statement: code.slice(index, i + 1), endIndex: i + 1 };
      }

      if (current === '\n') {
        return { statement: code.slice(index, i), endIndex: i + 1 };
      }
    }

    i++;
  }

  return { statement: code.slice(index), endIndex: code.length };
};

const splitLeadingImports = (code: string): { imports: string[]; body: string } => {
  const imports: string[] = [];
  let index = 0;

  while (true) {
    const statement = readImportStatement(code, index);
    if (!statement) break;

    imports.push(statement.statement.trim());
    index = statement.endIndex;
  }

  return {
    imports,
    body: code.slice(index).trimStart()
  };
};

const rewriteImportSpecifier = (statement: string, nextSpecifier: string): string => {
  if (/\bfrom\s+['"]/.test(statement)) {
    return statement.replace(/(\bfrom\s+)(['"])([^'"]+)(['"])/, `$1$2${nextSpecifier}$4`);
  }

  return statement.replace(/(\bimport\s*)(['"])([^'"]+)(['"])/, `$1$2${nextSpecifier}$4`);
};

const rewriteImports = async (
  imports: string[],
  baseUrl: string
): Promise<string[]> => {
  const rewritten: string[] = [];

  for (const statement of imports) {
    const fromMatch = statement.match(/\bfrom\s+['"]([^'"]+)['"]/);
    const sideEffectMatch = !fromMatch ? statement.match(/\bimport\s+['"]([^'"]+)['"]/) : null;
    const specifier = fromMatch?.[1] || sideEffectMatch?.[1];

    if (!specifier) {
      rewritten.push(statement);
      continue;
    }

    const resolved = defaultResolveModuleSpecifier(specifier, baseUrl);
    rewritten.push(rewriteImportSpecifier(statement, resolved));
  }

  return rewritten;
};

const createRequire = async (specifiers: string[], baseUrl: string): Promise<(specifier: string) => any> => {
  const cache: ModuleCache = {};

  await Promise.all(
    specifiers.map(async specifier => {
      const resolved = defaultResolveModuleSpecifier(specifier, baseUrl);
      const loaded = await import(/* @vite-ignore */ resolved);
      cache[specifier] = normalizeModule(loaded);
      cache[resolved] = cache[specifier];
    })
  );

  return (specifier: string) => {
    if (specifier in cache) {
      return cache[specifier];
    }

    throw new Error(`Module "${specifier}" is not available to require().`);
  };
};

export const executeModuleScript = async (
  code: string,
  context: Record<string, any>,
  prelude = ''
): Promise<ScriptExecutionResult> => {
  const logs: string[] = [];
  const baseUrl = typeof document !== 'undefined' ? document.baseURI : window.location.href;
  const { imports, body } = splitLeadingImports(code);
  const require = await createRequire(collectRequireSpecifiers(`${prelude}\n${body}`), baseUrl);
  const rewrittenImports = await rewriteImports(imports, baseUrl);

  const log = (...values: any[]) => {
    logs.push(values.map(formatLogValue).join(' '));
  };

  const consoleProxy = {
    ...console,
    log,
    info: log,
    warn: log,
    error: log
  };

  const moduleSource = `
${rewrittenImports.join('\n')}
export default async function(__context) {
  const input = __context.input ?? {};
  const variables = __context.variables ?? input;
  const log = __context.log;
  const require = __context.require;
  const console = __context.console;
  ${prelude}
  ${body}
}
`;

  const blobUrl = URL.createObjectURL(new Blob([moduleSource], { type: 'text/javascript' }));

  try {
    const moduleValue = await import(/* @vite-ignore */ blobUrl);
    const result = await moduleValue.default({
      ...context,
      input: context.input ?? context,
      variables: context.variables ?? context.input ?? context,
      log,
      require,
      console: consoleProxy
    });
    return { logs, result };
  } catch (error: any) {
    return { logs, result: undefined, error: error?.message || String(error) };
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
};
